/**
 * istemci.ts
 *
 * NE İŞE YARAR : Arka uca giden **tek** HTTP kapısı — başlıklar, gövde, hata çevrimi
 * KAYNAK SPEC  : 04-api-sozlesmesi.md §2.5 (standart başlıklar) · §2.6 (hata modeli)
 *                §2.7 (listeleme) · §7 (idempotency) · ADR-012 §2 (`If-Match`)
 * BAĞLI OLDUĞU : src/api/hata.ts · tarayıcının `fetch`i · vite.config.ts'teki `/api` vekili
 * KULLANAN     : src/oturum/baglam.tsx · src/ekran/**
 * DİKKAT       : 1) BU DOSYA OTURUMU TANIMAZ. Belirteç **parametreyle** gelir, depodan
 *                   okunmaz. Okusaydı kimlik ekranları (henüz oturumu olmayan çağrılar)
 *                   ikinci bir istek yolu yazmak zorunda kalırdı ve iki yoldan biri bir
 *                   gün başlığı unuturdu.
 *                2) TABAN ADRES `/api`DİR VE GÖMÜLÜ DEĞİLDİR. Arayüz her isteği kendi
 *                   kökünden yapar; gerçek adres yalnız `vite.config.ts` vekilinde
 *                   durur (o dosyanın notu 2). Üretimde de aynı köke sunulur.
 *                3) `Idempotency-Key` İSTEMCİ TARAFINDA ÜRETİLİR VE **YENİDEN DENEMEDE
 *                   DEĞİŞMEZ** (`04` §7). Her denemede yeni anahtar üretilseydi anahtarın
 *                   varlık sebebi ortadan kalkardı: aynı iş iki kez işlenirdi. Bu yüzden
 *                   anahtar çağıran tarafından verilir, burada üretilmez.
 *                4) `204` VE BOŞ GÖVDE `null` DÖNER, PATLAMAZ. `JSON.parse('')` atar ve
 *                   hata, sunucunun başarılı bir yanıtını başarısız gösterirdi.
 *                5) HATA GÖVDESİ OKUNAMAZSA UYDURULMAZ (`hata.ts` notu). Sunucunun ne
 *                   dediğini tahmin etmek, bir gün yanlış bir cümleyi ona mal etmektir.
 *                6) `X-Correlation-Id` YANITTAN OKUNUR (`04` §2.5). Kullanıcının
 *                   bildirdiği hatayı sunucu log'unda bulmanın tek yolu odur.
 * SON GÜNCELLEME: 2026-08-31 03:10:00
 */
import { API_SURUMU } from '../../../src/http/api-surumu';
import { ApiHatasi, baglantiHatasi, yanittanHata } from './hata';

/**
 * İsteklerin öntakısı — **sürüm dahil.**
 *
 * Önceden yalnız `/api` idi ve `/v1`i **`vite.config.ts`teki geliştirme vekili**
 * ekliyordu. Üretimde Vite yoktur: ters vekil yolu olduğu gibi geçirir, sunucu
 * `/api/auth/session` diye bir uç tanımaz ve **giriş `404` alır.** Arayüz üretimde ilk
 * kez çalıştırıldığında bu görüldü (2026-09-01).
 *
 * Sürüm burada da YAZILMAZ, `API_SURUMU`den gelir: iki yerde tutulsaydı biri
 * değiştiğinde diğeri sessizce eskirdi. Kapı **G22** tek kaynağı denetler.
 */
const TABAN = `/api/${API_SURUMU}`;

export type Yontem = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export interface IstekSecenekleri {
  /** `04` §4'teki yol — baştaki eğik çizgiyle: `/auth/session`. */
  readonly yol: string;
  readonly yontem?: Yontem;
  readonly govde?: unknown;
  /** Oturum belirteci; kimlik ekranlarında yoktur (başlıktaki not 1). */
  readonly belirtec?: string | null;
  /** `04` §7 — çağıran üretir ve yeniden denemede **aynısını** gönderir (not 3). */
  readonly idempotencyKey?: string;
  /** ADR-012 §2 — optimistic lock'lu işlemlerde zorunlu. */
  readonly ifMatch?: string;
  readonly iptalIsareti?: AbortSignal;
}

/**
 * İsteği gönderir ve gövdeyi çözer.
 *
 * Başarısızlıkta **her zaman** `ApiHatasi` atar; çağıran tek bir tip yakalar.
 */
export async function istek<T>(secenek: IstekSecenekleri): Promise<T> {
  const yontem = secenek.yontem ?? 'GET';
  const basliklar: Record<string, string> = { Accept: 'application/json' };

  if (secenek.belirtec !== undefined && secenek.belirtec !== null && secenek.belirtec !== '') {
    basliklar['Authorization'] = `Bearer ${secenek.belirtec}`;
  }
  if (secenek.idempotencyKey !== undefined) {
    basliklar['Idempotency-Key'] = secenek.idempotencyKey;
  }
  if (secenek.ifMatch !== undefined) {
    // Tırnak `04` §2.5'in biçimidir (`If-Match: "7"`), süs değil.
    basliklar['If-Match'] = `"${secenek.ifMatch}"`;
  }
  if (secenek.govde !== undefined) {
    basliklar['Content-Type'] = 'application/json';
  }

  let yanit: Response;
  try {
    yanit = await fetch(`${TABAN}${secenek.yol}`, {
      method: yontem,
      headers: basliklar,
      ...(secenek.govde === undefined ? {} : { body: JSON.stringify(secenek.govde) }),
      ...(secenek.iptalIsareti === undefined ? {} : { signal: secenek.iptalIsareti }),
    });
  } catch (sebep) {
    // Ağ katmanı hiç cevap veremedi — `15` §9'un *"bağlantı yok"* durumu.
    throw baglantiHatasi(sebep);
  }

  const correlationId = yanit.headers.get('X-Correlation-Id');
  const ham = await govdeyiCoz(yanit);

  if (!yanit.ok) {
    throw yanittanHata(yanit.status, ham, correlationId);
  }

  return ham as T;
}

/**
 * Gövdeyi JSON olarak çözer; boş ya da JSON olmayan gövdede `null` döner.
 *
 * Başarılı yanıtlarda başlıktaki not 4'ün sebebi budur. Başarısız yanıtlarda ise
 * `null`, `yanittanHata`nın `COZUMLENEMEDI` yoluna düşer.
 */
async function govdeyiCoz(yanit: Response): Promise<unknown> {
  const ham = await yanit.text().catch(() => '');
  if (ham === '') {
    return null;
  }
  try {
    return JSON.parse(ham) as unknown;
  } catch {
    return null;
  }
}

/**
 * `04` §7 için idempotency anahtarı üretir.
 *
 * `crypto.randomUUID` her modern tarayıcıda vardır ve **güvenli** rastgeleliktir;
 * `Math.random` tabanlı bir üretici, aynı anahtarın iki farklı isteğe düşme ihtimalini
 * ölçülemez hâle getirirdi.
 */
export function yeniIdempotencyKey(): string {
  return crypto.randomUUID();
}

export { ApiHatasi };
